import { useEffect, useMemo, useState } from "react";
import { Card } from "primereact/card";
import CustomeButton from "~/components/Button";
import PayslipMessage from "./PayslipMessage";
import PayslipTable from "./PayslipTable";
import PayslipDeleteModal from "./PayslipDeleteModal";
import usePayslipUpload from "~/Pages/Process/actions/usePayslipUpload";
import ToastMessage from "~/components/ToastMessage";
import { Skeleton } from "primereact/skeleton";
import PayslipSubmissionModal from "./PayslipSubmissionModal";
import AttachmentSuccess from "~/components/toast-notification/AttachmentSuccess";

const PayslipUploadCard = ({
  openModal,
  processId,
  data = [],
  loading,
  isDataAvailable = false,
  showPayslipTable,
  fetchPayslipMatching,
  isOwner,
}) => {
  const {
    showModal,
    handleCloseModal,
    handleDelete,
    isLoading,
    fileData,
    toastRef,
    handleConfirm,
    restoreMessage,
    setRstoreMessage,
  } = usePayslipUpload({ processId, fetchPayslipMatching });
  const [isCompleteBtnDisabled, setIsCompleteBtnDisabled] = useState(true);
  const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState(false);

  const message = {
    processing: {
      severity: "info",
      text: "One or more files are being processed. Additional input may be required once the process is complete.",
      className: "final-payroll-info",
      icon: <span className="pi pi-spin pi-spinner mr-1 spinner-info" />,
    },
    awaiting: {
      severity: "warn",
      text: "One or more files are ready for review and submission.",
      className: "final-payroll-warning",
      icon: null,
    },
    approved: {
      severity: "Success",
      text: 'One or more files have been reviewed and submitted. You may now mark this task as complete only when all files are "Submitted" and no more files are pending to upload.',
      className: "final-payroll-complete",
      icon: <i className="pi pi-check mr-2"></i>,
    },
  };

  function resolveStatus(rows = []) {
    const hasProcessing = rows.some(
      (r) =>
        r?.match_status === "" ||
        r?.match_status?.toString().trim().toLowerCase() === "pending",
    );
    const hasAwaiting = rows.some((r) =>
      ["matched", "unmatched"].includes(
        r?.match_status?.toString().trim().toLowerCase(),
      ),
    );
    const hasApproved = rows.some(
      (r) => r?.match_status?.toString().trim().toLowerCase() === "approved",
    );

    if (hasProcessing && hasAwaiting && !hasApproved)
      return ["processing", "awaiting"];
    if (hasProcessing) return ["processing"];
    if (hasAwaiting) return ["awaiting"];
    if (hasApproved && !hasProcessing && !hasAwaiting) return ["approved"];

    return null;
  }

  const filteredData = useMemo(() => {
    // Group records by message ID
    const messageGroups = data.reduce((groups, record) => {
      const messageId = record.payslipMessageData?.id;
      if (!groups[messageId]) {
        groups[messageId] = [];
      }
      groups[messageId].push(record);
      return groups;
    }, {});

    const filteredRecords = [];

    Object.values(messageGroups).forEach((group) => {
      // Find source file in this group
      const sourceFile = group.find(
        (record) =>
          record.extracted_data?.source_file === true &&
          record.match_status === "source",
      );

      // Get all non-source files in this group
      const nonSourceFiles = group.filter(
        (record) =>
          !(
            record.extracted_data?.source_file === true &&
            record.match_status === "source"
          ),
      );

      // Check if all non-source files are approved or rejected
      const allNonSourceApprovedOrRejected = nonSourceFiles.every(
        (record) =>
          record.match_status === "approved" ||
          record.match_status === "rejected",
      );

      // Add non-source files with filtering logic
      nonSourceFiles.forEach((record) => {
        // Filter out page files unless they're approved or rejected
        if (
          record.extracted_data?.page_number !== undefined &&
          record.extracted_data?.page_number !== null &&
          record.extracted_data?.page_number !== ""
        ) {
          if (
            record.match_status === "approved" ||
            record.match_status === "rejected"
          ) {
            filteredRecords.push(record);
          }
          // Skip page files that are not approved or rejected
        } else {
          // Add non-page files (regular payslips)
          filteredRecords.push(record);
        }
      });

      // Add source file only if not all other files are approved/rejected
      if (sourceFile && !allNonSourceApprovedOrRejected) {
        filteredRecords.push(sourceFile);
      }
    });

    return filteredRecords;
  }, [data]);
  const currentMessageKey = useMemo(() => {
    if (!isDataAvailable || !showPayslipTable || loading) return null;
    return resolveStatus(filteredData);
  }, [filteredData, isDataAvailable, showPayslipTable, loading]);

  useEffect(() => {
    // Check approval for all non-source files in the original data (not filtered)
    const nonSourceFiles = data.filter(
      (file) =>
        !(
          file.extracted_data?.source_file === true &&
          file.match_status === "source"
        ),
    );

    // Check if all non-source files are approved
    const allNonSourceApproved =
      nonSourceFiles.length > 0 &&
      nonSourceFiles.every((file) => file?.match_status === "approved");

    // Additionally check that all split files (files with page_number) are approved
    const splitFiles = data.filter(
      (file) =>
        file.extracted_data?.page_number !== undefined &&
        file.extracted_data?.page_number !== null &&
        file.extracted_data?.page_number !== "",
    );

    const allSplitFilesApproved =
      splitFiles.length === 0 ||
      splitFiles.every((file) => file?.match_status === "approved");

    const result = allNonSourceApproved && allSplitFilesApproved;
    const disableBtn = !(result && showPayslipTable && isOwner);
    setIsCompleteBtnDisabled(disableBtn);
  }, [data, showPayslipTable, isOwner]);

  const completeBtnHandler = () => {
    setIsConfirmDialogVisible(true);
  };

  const closeModalHanlder = () => {
    setIsConfirmDialogVisible(false);
  };

  return (
    <>
      <Card className="mb-4 payslip-card p-5">
        <div className="payslip-card-content flex flex-column gap-1">
          <p className="card-title">Submit Final Payroll Output</p>
          <p className="card-description">
            You're now ready to submit final documents for this payroll cycle.
            Please upload payslips and any additional files required for
            employee release
          </p>
        </div>
        <div className="flex justify-content-between">
          {isOwner &&
            (loading ? (
              <Skeleton width="11rem" height="3rem" />
            ) : (
              <CustomeButton
                label="Upload Payslips"
                icon="pi pi-upload"
                iconPos="right"
                onClick={openModal}
                variant={data.length ? "outlined" : "contained"}
              />
            ))}

          {isOwner &&
            (loading ? (
              <Skeleton width="11rem" height="3rem" />
            ) : data.length ? (
              <CustomeButton
                label="Mark as Complete"
                className="max-w-max"
                variant="contained"
                icon="pi pi-check-square"
                iconPos="right"
                onClick={completeBtnHandler}
                disabled={isCompleteBtnDisabled}
              />
            ) : null)}
        </div>

        {isDataAvailable && showPayslipTable && !loading && (
          <div className="flex flex-column gap-2">
            {currentMessageKey &&
              currentMessageKey.map((key) => (
                <PayslipMessage key={key} data={message[key]} />
              ))}
          </div>
        )}
        <AttachmentSuccess
          successMsg={restoreMessage}
          setMessages={setRstoreMessage}
        />
        {isDataAvailable && showPayslipTable && (
          <PayslipTable
            data={filteredData}
            loading={loading || isLoading}
            processId={processId}
            handleConfirm={handleConfirm}
            isOwner={isOwner}
          />
        )}
        <PayslipDeleteModal
          showModal={showModal}
          handleCloseModal={handleCloseModal}
          handleDelete={handleDelete}
          isLoading={isLoading}
          fileData={fileData}
        />
        <ToastMessage ref={toastRef} />
      </Card>

      {isConfirmDialogVisible && (
        <PayslipSubmissionModal
          visible={isConfirmDialogVisible}
          closeModal={closeModalHanlder}
          toastRef={toastRef}
        />
      )}
    </>
  );
};

export default PayslipUploadCard;
